import { useState, useEffect, useRef } from 'react';

/**
 * Reveal-on-scroll. Returns a ref and a boolean.
 * Combine with the .reveal / .reveal-left / .reveal-right / .reveal-scale CSS classes
 * by toggling the `is-in` class, or use the isVisible boolean directly.
 */
export function useScrollAnimation(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

export function useStaggeredAnimation(_itemCount = 0, threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  const getDelay = (index: number) => {
    if (!isVisible) return '0ms';
    return `${100 + index * 110}ms`;
  };

  return { ref, isVisible, getDelay };
}

/**
 * Parallax hook — moves an element based on how far it has scrolled through the viewport.
 * `speed` positive = moves up as you scroll down (slower than page), negative = opposite.
 * Returns a ref and the current translateY in px.
 */
export function useParallax(speed = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const winH = window.innerHeight || 1;
      // progress: -1 (below screen) .. 0 (centered) .. 1 (above screen)
      const progress = (rect.top + rect.height / 2 - winH / 2) / winH;
      setOffset(-progress * speed * 100);
      raf = 0;
    };

    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [speed]);

  return { ref, offset };
}